<script>
    import { onMount } from 'svelte';
    import { NICHE_TEMPLATES, getNicheTemplate } from '../data/nicheTemplates.js';
    import { createComplementGroupsBatch, updateStoreSettings, fetchStoreSettings, updateAiFunnel } from '../services/api.js';
    import { syncGroups } from '../stores/complementsStore.js';
    import Icon from './icons/Icon.svelte';

    // Sub-componentes modulares da experiência UX/UI
    import NicheSelector from './onboarding/NicheSelector.svelte';
    import SmartSetupSummary from './onboarding/SmartSetupSummary.svelte';
    import ComplementGroupsList from './onboarding/ComplementGroupsList.svelte';
    import ComplementGroupEditor from './onboarding/ComplementGroupEditor.svelte';
    import OnboardingFinishStep from './onboarding/OnboardingFinishStep.svelte';

    export let onComplete = () => {};
    export let onNotify = (msg, type) => {};
    export let isStandaloneImporter = false;

    // Etapas do Wizard:
    // 1: Qual é o seu negócio? (NicheSelector)
    // 2: Preparamos uma base para você (SmartSetupSummary)
    // 3: Revise seus complementos (ComplementGroupsList)
    //    (Se editingGroupKey !== null -> ComplementGroupEditor)
    // 4: WhatsApp, PIX & Conclusão (OnboardingFinishStep)
    let step = isStandaloneImporter ? 3 : 1;
    let editingGroupKey = null;

    let isLoading = true;
    let isSubmitting = false;

    // Dados da Loja
    let storeName = '';
    let storePhone = '';
    let storeColor = '#FF6A00';
    let pixKey = '';
    let pixKeyType = 'cpf';
    let defaultDeliveryFee = 5.00;
    let estimatedMinutes = 45;

    // Mensagens do Bot do WhatsApp
    let botWelcomeMsg = '';
    let botUpsellMsg = '';

    // Nicho Selecionado
    let selectedNicheId = 'hamburgueria';
    let selectedNiche = NICHE_TEMPLATES[0];

    // Estado dos Grupos de Complementos do Nicho
    let editableGroups = [];

    onMount(async () => {
        try {
            const data = await fetchStoreSettings();
            if (data) {
                storeName = data.name || storeName || '';
                storePhone = data.phone || storePhone || '';
                storeColor = data.primary_color || '#FF6A00';
                pixKey = data.pix_key || '';
                pixKeyType = data.pix_key_type || 'cpf';
                defaultDeliveryFee = data.default_delivery_fee !== undefined ? data.default_delivery_fee : 5.00;
                estimatedMinutes = data.estimated_minutes || 45;
            }
        } catch (e) {
            console.warn('[Onboarding] Não foi possível obter dados prévios da loja:', e.message);
        }

        selectNiche(selectedNicheId);
        isLoading = false;
    });

    function selectNiche(nicheId) {
        selectedNicheId = nicheId;
        selectedNiche = getNicheTemplate(nicheId);
        storeColor = selectedNiche.themeColor || '#FF6A00';

        // Preenche sugestão de mensagens do bot baseadas no nicho
        if (selectedNiche.botMessages) {
            botWelcomeMsg = selectedNiche.botMessages.welcome || '';
            botUpsellMsg = selectedNiche.botMessages.upsell || '';
        }

        // Clona profundamente os grupos para permitir edição individual completa
        editableGroups = selectedNiche.groups.map((g, gIdx) => ({
            key: g.key || `g_${gIdx}`,
            name: g.name,
            badge: g.badge,
            required: g.required,
            min_choice: g.min_choice,
            max_choice: g.max_choice,
            free_choices: g.free_choices,
            enabled: g.defaultSelected !== false,
            items: g.items.map((it, itIdx) => ({
                id: `it_${gIdx}_${itIdx}_${Date.now()}`,
                name: it.name,
                price: it.price,
                enabled: it.defaultSelected !== false
            }))
        }));
    }

    function handleToggleGroup(groupKey) {
        const g = editableGroups.find(x => x.key === groupKey);
        if (g) {
            g.enabled = !g.enabled;
            editableGroups = [...editableGroups];
        }
    }

    function handleEditGroup(groupKey) {
        editingGroupKey = groupKey;
    }

    function handleCloseEditor() {
        editingGroupKey = null;
    }

    function handleAddGroup() {
        const newKey = `g_custom_${Date.now()}`;
        const newGroup = {
            key: newKey,
            name: 'Nova Categoria de Complementos',
            required: false,
            min_choice: 0,
            max_choice: 1,
            free_choices: 0,
            enabled: true,
            items: [
                { id: `it_${Date.now()}_1`, name: 'Opção 1', price: 0, enabled: true }
            ]
        };
        editableGroups = [...editableGroups, newGroup];
        editingGroupKey = newKey;
    }

    function handleDeleteGroup(groupKey) {
        editableGroups = editableGroups.filter(g => g.key !== groupKey);
        editingGroupKey = null;
    }

    // Salvar e Finalizar
    async function handleFinishAll() {
        isSubmitting = true;
        try {
            // 1. Cria os complementos no banco de dados
            let createdCount = 0;
            const payloadGroups = editableGroups
                .filter(g => g.enabled && g.name && g.name.trim())
                .map((g, idx) => ({
                    name: g.name.trim(),
                    required: g.required ? 1 : 0,
                    min_choice: g.min_choice,
                    max_choice: g.max_choice,
                    free_choices: g.free_choices,
                    display_order: idx + 1,
                    items: (g.items || [])
                        .filter(i => i.enabled && i.name && i.name.trim())
                        .map((i, itIdx) => ({
                            name: i.name.trim(),
                            price: parseFloat(i.price || 0),
                            display_order: itIdx + 1
                        }))
                }));

            if (payloadGroups.length > 0) {
                await createComplementGroupsBatch(payloadGroups);
                await syncGroups();
                createdCount = payloadGroups.length;
            }

            // 2. Se for onboarding completo, atualiza configurações da loja e funil do bot
            if (!isStandaloneImporter) {
                await updateStoreSettings({
                    name: storeName.trim() || selectedNiche.name,
                    phone: storePhone.trim(),
                    primary_color: storeColor,
                    pix_key: pixKey.trim(),
                    pix_key_type: pixKeyType,
                    default_delivery_fee: parseFloat(defaultDeliveryFee || 0),
                    estimated_minutes: parseInt(estimatedMinutes || 45, 10),
                    business_profile: selectedNicheId,
                    onboarding_completed: 1
                });

                // Sincroniza em tempo real com o funil do robô de WhatsApp
                try {
                    await updateAiFunnel({
                        welcome_message: botWelcomeMsg,
                        upsell_message: botUpsellMsg,
                        pix_key: pixKey.trim(),
                        pix_key_type: pixKeyType,
                        accept_pix: pixKey.trim() ? 1 : 0
                    });
                } catch (funnelErr) {
                    console.warn('[Onboarding] Aviso ao sincronizar funil do bot de IA:', funnelErr.message);
                }
            }

            const successMsg = createdCount > 0
                ? `Sucesso! ${createdCount} grupos de complementos do nicho "${selectedNiche.name}" foram adicionados ao seu cardápio.`
                : 'Configurações iniciais salvas com sucesso! Bem-vindo ao MataFome.';

            onNotify(successMsg, 'success');
            onComplete();
        } catch (err) {
            console.error('[Onboarding] Erro ao finalizar:', err);
            onNotify(`Erro ao salvar: ${err.message}`, 'error');
        } finally {
            isSubmitting = false;
        }
    }

    $: currentEditingGroup = editableGroups.find(g => g.key === editingGroupKey);
</script>

<div class="onboarding-clean-container">
    {#if isLoading}
        <div class="loading-state">
            <div class="clean-spinner"></div>
            <p>Carregando catálogo profissional do MataFome...</p>
        </div>
    {:else}
        <!-- TOPO EXECUTIVO COM LOGO OFICIAL DO MATAFOME E STEPPER -->
        <header class="wizard-header">
            <div class="header-inner">
                <!-- Logotipo Oficial MataFome Limpo (Sem texto redundante) -->
                <div class="brand-logo-wrap">
                    <img
                        src="./brand/matafome_logo_light.png"
                        alt="MataFome"
                        class="matafome-logo-img"
                        on:error={(e) => { e.target.src = './brand/matafome_logo_horizontal.png'; }}
                    />
                </div>

                <!-- Stepper Minimalista e Arejado -->
                {#if !isStandaloneImporter}
                    <nav class="stepper-nav" aria-label="Progresso do onboarding">
                        <div class="step-item {step >= 1 ? 'is-active' : ''} {step > 1 ? 'is-done' : ''}">
                            <span class="step-circle">{step > 1 ? '✓' : '1'}</span>
                            <span class="step-text">Negócio</span>
                        </div>
                        <div class="step-line {step >= 2 ? 'is-filled' : ''}"></div>

                        <div class="step-item {step >= 2 ? 'is-active' : ''} {step > 2 ? 'is-done' : ''}">
                            <span class="step-circle">{step > 2 ? '✓' : '2'}</span>
                            <span class="step-text">Automação</span>
                        </div>
                        <div class="step-line {step >= 3 ? 'is-filled' : ''}"></div>

                        <div class="step-item {step >= 3 ? 'is-active' : ''} {step > 3 ? 'is-done' : ''}">
                            <span class="step-circle">{step > 3 ? '✓' : '3'}</span>
                            <span class="step-text">Complementos</span>
                        </div>
                        <div class="step-line {step >= 4 ? 'is-filled' : ''}"></div>

                        <div class="step-item {step >= 4 ? 'is-active' : ''}">
                            <span class="step-circle">4</span>
                            <span class="step-text">Recebimento</span>
                        </div>
                    </nav>
                {/if}
            </div>
        </header>

        <!-- ÁREA CENTRAL DE CONTEÚDO (MAX-WIDTH 860PX, RESPIRO VISUAL) -->
        <main class="wizard-body">
            {#if step === 1}
                <!-- ETAPA 1: QUAL É O SEU NEGÓCIO? -->
                <NicheSelector
                    {selectedNicheId}
                    bind:storeName
                    bind:storePhone
                    onSelectNiche={(nicheId) => selectNiche(nicheId)}
                    onContinue={() => { step = 2; }}
                />

            {:else if step === 2}
                <!-- ETAPA 2: APRESENTAR CONFIGURAÇÃO INTELIGENTE -->
                <SmartSetupSummary
                    {selectedNiche}
                    groups={editableGroups}
                    onChangeNiche={() => { step = 1; }}
                    onReview={() => { step = 3; }}
                />

            {:else if step === 3}
                <!-- ETAPA 3 & 4: REVISÃO DE GRUPOS OU EDITOR FOCADO -->
                {#if editingGroupKey && currentEditingGroup}
                    <!-- ETAPA 4: EDITAR GRUPO FOCADO -->
                    <ComplementGroupEditor
                        group={currentEditingGroup}
                        onBack={handleCloseEditor}
                        onDeleteGroup={handleDeleteGroup}
                    />
                {:else}
                    <!-- ETAPA 3: LISTA RESUMIDA DOS GRUPOS -->
                    <ComplementGroupsList
                        groups={editableGroups}
                        onEditGroup={handleEditGroup}
                        onToggleGroup={handleToggleGroup}
                        onAddGroup={handleAddGroup}
                        onBack={() => {
                            if (isStandaloneImporter) {
                                onComplete();
                            } else {
                                step = 2;
                            }
                        }}
                        onContinue={() => {
                            if (isStandaloneImporter) {
                                handleFinishAll();
                            } else {
                                step = 4;
                            }
                        }}
                    />
                {/if}

            {:else if step === 4}
                <!-- ETAPA 4: RECEBIMENTO, MERCADO PAGO, ENTREGA & CONCLUSÃO -->
                <OnboardingFinishStep
                    {selectedNiche}
                    bind:pixKey
                    bind:pixKeyType
                    bind:defaultDeliveryFee
                    bind:estimatedMinutes
                    {isSubmitting}
                    {onNotify}
                    onBack={() => { step = 3; }}
                    onFinish={handleFinishAll}
                />
            {/if}
        </main>
    {/if}
</div>

<style>
    /* CONTAINER PRINCIPAL LIGHT & CLEAN */
    .onboarding-clean-container {
        display: flex;
        flex-direction: column;
        min-height: 100%;
        max-height: 94vh;
        background: #F8FAFC;
        color: #0F172A;
        font-family: inherit;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: #CBD5E1 transparent;
    }

    .onboarding-clean-container::-webkit-scrollbar {
        width: 8px;
    }

    .onboarding-clean-container::-webkit-scrollbar-track {
        background: #F1F5F9;
    }

    .onboarding-clean-container::-webkit-scrollbar-thumb {
        background: #CBD5E1;
        border-radius: 6px;
    }

    .onboarding-clean-container::-webkit-scrollbar-thumb:hover {
        background: #94A3B8;
    }

    .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 100px 20px;
        gap: 16px;
        color: #64748B;
    }

    .clean-spinner {
        width: 38px;
        height: 38px;
        border: 3px solid #E2E8F0;
        border-top-color: #FF6A00;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    /* HEADER */
    .wizard-header {
        background: #FFFFFF;
        border-bottom: 1px solid #E2E8F0;
        padding: 16px 32px;
        position: sticky;
        top: 0;
        z-index: 20;
    }

    .header-inner {
        max-width: 920px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }

    .brand-logo-wrap {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .matafome-logo-img {
        height: 48px;
        width: auto;
        object-fit: contain;
        image-rendering: -webkit-optimize-contrast;
    }

    /* STEPPER */
    .stepper-nav {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .step-item {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #94A3B8;
        font-size: 12px;
        font-weight: 600;
        transition: color 0.15s;
    }

    .step-item.is-active {
        color: #0F172A;
    }

    .step-item.is-done {
        color: #059669;
    }

    .step-circle {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #F1F5F9;
        color: #64748B;
        font-size: 11px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.15s;
    }

    .step-item.is-active .step-circle {
        background: #FF6A00;
        color: #FFFFFF;
    }

    .step-item.is-done .step-circle {
        background: #059669;
        color: #FFFFFF;
    }

    .step-line {
        width: 20px;
        height: 2px;
        background: #E2E8F0;
        transition: background 0.15s;
    }

    .step-line.is-filled {
        background: #059669;
    }

    /* BODY */
    .wizard-body {
        max-width: 920px;
        margin: 0 auto;
        width: 100%;
        padding: 32px 24px 60px 24px;
        flex: 1;
    }

    @media (max-width: 768px) {
        .wizard-header {
            padding: 14px 16px;
        }

        .header-inner {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }

        .stepper-nav {
            width: 100%;
            justify-content: space-between;
        }

        .step-text {
            display: none;
        }

        .wizard-body {
            padding: 20px 16px 40px 16px;
        }
    }
</style>
